import React, { useEffect, useMemo } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { CARGO_CODES } from "constants/permissions";
import { AuthState } from "store/ducks/auth/types";
import SelectAutoCompleteMultiple from "components/SelectAutoCompleteMultipleG";
import DatePickerRangeG from "components/DatePickerRangeG";
import CheckboxComponent from "components/CheckBoxG";
import SelectComponent from "components/selectAutoCompleteG";
import { useListarEmpreendimentosFunil } from "services/api/Funil_Propostas/listar-empreendimentos";
import { useListarTorres } from "services/api/AppWeb_Torre/torres";
import { useListarUnidadesFunil } from "services/api/Funil_Propostas/listar-unidades";
import {
  PerfilHierarquico,
  useUsuariosPorPerfilHierarquico,
} from "services/api/Identity_Usuario/usuarios-por-perfil-hierarquico";
import { useListarOrigensLeads } from "services/api/Mobile_OrigemLead/origens-leads";
import { obterNomeEtapaExibicao } from "utils/obterNomeEtapaExibicao";
import { DivisorContainer } from "components/divisorContainer";
import * as S from "./styles";
import * as IconsSVG from "../../../../assets/iconsSVG";

interface Option {
  value: string;
  label: string;
  codigoSap?: string;
}

interface EtapasItem {
  codigo: string;
  nome: string;
}

interface Props {
  etapas: EtapasItem[];
  subEtapas: EtapasItem[];
}

interface Data {
  isOpen: boolean;
  selectedEtapa: Option | null;
  handleEtapaChange: (newValue: Option | null) => void;
  selectedSubetapa: Option[];
  handleSubetapaChange: (
    event: React.SyntheticEvent<Element, Event>,
    newValues: Option[] | null
  ) => void;
  selectedEmpreendimento: Option[];
  handleEmpreendimentoChange: (
    event: React.SyntheticEvent<Element, Event>,
    newValues: Option[] | null
  ) => void;
  selectedBloco: Option | null;
  handleBlocoChange: (newValue: Option | null) => void;
  selectedUnidade: Option | null;
  handleUnidadeChange: (newValue: Option | null) => void;
  selectedOrigens: Option[];
  handleOrigensChange: (
    event: React.SyntheticEvent<Element, Event>,
    newValues: Option[] | null
  ) => void;
  selectedSuperintendente: Option | null;
  handleSuperintendenteChange: (newValue: Option | null) => void;
  selectedEquipe: Option | null;
  handleEquipeChange: (newValue: Option | null) => void;
  selectedCorretor: Option | null;
  handleCorretorChange: (newValue: Option | null) => void;
  selectedTipoFiltroData: Option | null;
  handleTipoFiltroDataChange: (newValue: Option | null) => void;
  tipoFiltroDataOptions: Option[];
  dataInicio: Date | null;
  dataFim: Date | null;
  handleDataInicioChange: (newDate: Date | null) => void;
  handleDataFimChange: (newDate: Date | null) => void;
  checkCompartilharProposta: boolean;
  setCheckCompartilharProposta: (value: boolean) => void;
  handleClearFilters: () => void;
  handleApplyFilters: () => void;
  dataEtapasFiltro: Props;
  dataSubEtapasFiltro: Props;
  isFetchingSubEtapasFiltro: boolean;
}

const Filtro = (data: Data) => {
  const { codigoCargo2013, usuarioDiretor, usuarioGerenteOnline } = useSelector<
    RootStateOrAny,
    AuthState
  >((state) => state.user);

  const isDiretor = usuarioDiretor === true;
  const showDiretorFilters = isDiretor || usuarioGerenteOnline;
  const isSuperintendente =
    codigoCargo2013 === CARGO_CODES.SUPERINTENDENTE && !isDiretor;
  const showHierarchy = showDiretorFilters || isSuperintendente;

  const hasEmpreendimentoSelecionado = data.selectedEmpreendimento.length > 0;
  const empreendimentoSelecionado = hasEmpreendimentoSelecionado
    ? data.selectedEmpreendimento[0]
    : null;

  const codigoSapEmpreendimento =
    empreendimentoSelecionado?.codigoSap ||
    empreendimentoSelecionado?.value ||
    null;

  const { data: dataEmpreendimentos } = useListarEmpreendimentosFunil();

  const {
    data: dataBlocos,
    refetch: refetchBlocos,
    isFetching: isFetchingBlocos,
  } = useListarTorres({
    idEmpreendimento: empreendimentoSelecionado?.value,
    queryKey: `funil-propostas-blocos-${
      empreendimentoSelecionado?.value || "none"
    }`,
  });

  const {
    data: dataUnidades,
    refetch: refetchUnidades,
    isFetching: isFetchingUnidades,
  } = useListarUnidadesFunil({
    queryKey: `funil-propostas-unidades-${codigoSapEmpreendimento || "none"}-${
      data.selectedBloco?.label || "none"
    }`,
    codigoSapEmpreendimento,
    nomeBlocoEmpreendimento: data.selectedBloco?.label || null,
  });

  const { data: dataOrigens, refetch: refetchOrigens } = useListarOrigensLeads({
    filtro: true,
  });

  const { data: dataSuperintendentes } = useUsuariosPorPerfilHierarquico({
    queryKey: "filtro-funil-propostas-superintendentes",
    perfil: PerfilHierarquico.Superintendente,
    enabled: data.isOpen && showDiretorFilters,
  });

  const { data: dataEquipes } = useUsuariosPorPerfilHierarquico({
    queryKey: `filtro-funil-propostas-equipes-${
      showDiretorFilters
        ? data.selectedSuperintendente?.value || "self"
        : "self"
    }`,
    perfil: PerfilHierarquico.Gerente,
    idLider: showDiretorFilters
      ? data.selectedSuperintendente?.value || null
      : null,
    enabled:
      data.isOpen &&
      ((showDiretorFilters && Boolean(data.selectedSuperintendente)) ||
        isSuperintendente),
  });

  const { data: dataCorretores } = useUsuariosPorPerfilHierarquico({
    queryKey: `filtro-funil-propostas-corretores-${
      data.selectedEquipe?.value || "all"
    }`,
    perfil: PerfilHierarquico.Corretor,
    idLider: data.selectedEquipe?.value || null,
    enabled: data.isOpen && showHierarchy,
  });

  useEffect(() => {
    if (!data.isOpen || !hasEmpreendimentoSelecionado) return;
    refetchBlocos();
  }, [
    data.isOpen,
    hasEmpreendimentoSelecionado,
    empreendimentoSelecionado?.value,
    refetchBlocos,
  ]);

  useEffect(() => {
    if (!data.isOpen || !hasEmpreendimentoSelecionado || !data.selectedBloco)
      return;
    refetchUnidades();
  }, [
    data.isOpen,
    hasEmpreendimentoSelecionado,
    codigoSapEmpreendimento,
    data.selectedBloco?.label,
    refetchUnidades,
  ]);

  useEffect(() => {
    if (!data.isOpen) return;
    refetchOrigens();
  }, [data.isOpen, refetchOrigens]);

  const empreendimentoOptions = useMemo(
    () =>
      dataEmpreendimentos?.data
        ?.map((empreendimento: any) => ({
          value:
            empreendimento.id ||
            empreendimento.idEmpreendimento ||
            empreendimento.codigoSap ||
            "",
          label: empreendimento.nome || empreendimento.nomeEmpreendimento || "",
          codigoSap:
            empreendimento.codigoSap ||
            empreendimento.codigo ||
            empreendimento.id ||
            "",
        }))
        .filter((item: Option) => item.value && item.label) || [],
    [dataEmpreendimentos]
  );

  const blocosOptions = useMemo(
    () =>
      dataBlocos?.data
        ?.map((bloco: any) => ({
          value: bloco.id || bloco.idTorre || "",
          label: bloco.nome || bloco.nomeBloco || "",
        }))
        .filter((item: Option) => item.value && item.label) || [],
    [dataBlocos]
  );

  const unidadesOptions = useMemo(
    () =>
      dataUnidades?.data
        ?.map((unidade: any) => ({
          value: String(
            unidade.idUnidade ||
              unidade.id ||
              unidade.codigoUnidade ||
              unidade.codigo ||
              ""
          ),
          label:
            unidade.nomeUnidade ||
            unidade.unidade ||
            unidade.nome ||
            unidade.codigoUnidade ||
            unidade.codigo ||
            "",
        }))
        .filter((item: Option) => item.value && item.label) || [],
    [dataUnidades]
  );

  const origensOptions = useMemo(
    () =>
      dataOrigens?.data
        ?.map((origem: any) => ({
          value: origem.id,
          label: origem.nome,
        }))
        .filter((item: Option) => item.value && item.label) || [],
    [dataOrigens]
  );

  const superintendentesOptions = useMemo(
    () =>
      dataSuperintendentes
        ?.map((superintendente) => ({
          value: superintendente.idUsuario || superintendente.id,
          label: superintendente.nome,
        }))
        .filter((item) => item.value && item.label) || [],
    [dataSuperintendentes]
  );

  const equipesOptions = useMemo(
    () =>
      dataEquipes
        ?.map((equipe) => ({
          value: equipe.idUsuario || equipe.id,
          label: equipe.nome,
        }))
        .filter((item) => item.value && item.label) || [],
    [dataEquipes]
  );

  const corretoresOptions = useMemo(
    () =>
      dataCorretores
        ?.map((corretor) => ({
          value: corretor.id,
          label: corretor.nome,
        }))
        .filter((item) => item.value && item.label) || [],
    [dataCorretores]
  );

  const { usuarioVivaz } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.user
  );
  return (
    <S.Content>
      <S.Row style={{ justifyContent: "space-between" }}>
        <IconsSVG.FilterModalIcon />
        <S.TextFilter>Filtros</S.TextFilter>
        <S.ButtonClear onClick={data.handleClearFilters}>Limpar</S.ButtonClear>
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <S.DateFilterContainer>
          <S.DateFilterTitle>Filtrar por</S.DateFilterTitle>
          <S.RadioGroup
            aria-labelledby="tipo-filtro-data-funil-propostas"
            value={data.selectedTipoFiltroData?.value || ""}
            name="tipoFiltroDataFunilPropostas"
            onChange={(event) => {
              const option =
                data.tipoFiltroDataOptions.find(
                  (item) => item.value === event.target.value
                ) || null;
              data.handleTipoFiltroDataChange(option);
            }}
          >
            {data.tipoFiltroDataOptions.map((option) => (
              <S.FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <S.Radio
                    checked={
                      data.selectedTipoFiltroData?.value === option.value
                    }
                    onClick={() => {
                      if (data.selectedTipoFiltroData?.value === option.value) {
                        data.handleTipoFiltroDataChange(null);
                      }
                    }}
                  />
                }
                label={option.label}
              />
            ))}
          </S.RadioGroup>
        </S.DateFilterContainer>
      </S.Row>
      {data.selectedTipoFiltroData && (
        <>
          <S.SpaceY space={12} />
          <S.Row>
            <DatePickerRangeG
              startDate={data.dataInicio}
              endDate={data.dataFim}
              onStartDateChange={data.handleDataInicioChange}
              onEndDateChange={data.handleDataFimChange}
              width="100%"
              disabled={false}
            />
          </S.Row>
        </>
      )}
      <S.SpaceY space={32} />
      <S.Row>
        <SelectAutoCompleteMultiple
          borderRadius="8px"
          data={empreendimentoOptions}
          placeholder="Selecione"
          label="Empreendimento"
          value={data.selectedEmpreendimento || []}
          disabled={false}
          onChange={data.handleEmpreendimentoChange}
        />
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <SelectComponent
          borderRadius="8px"
          data={blocosOptions}
          placeholder="Selecione"
          label="Bloco"
          value={data.selectedBloco}
          disabled={!hasEmpreendimentoSelecionado || isFetchingBlocos}
          onChange={data.handleBlocoChange}
        />
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <SelectComponent
          borderRadius="8px"
          data={unidadesOptions}
          placeholder="Selecione"
          label="Unidade"
          value={data.selectedUnidade}
          disabled={!data.selectedBloco || isFetchingUnidades}
          onChange={data.handleUnidadeChange}
        />
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <SelectAutoCompleteMultiple
          borderRadius="8px"
          data={origensOptions}
          placeholder="Selecione"
          label="Origem"
          value={data.selectedOrigens || []}
          disabled={false}
          onChange={data.handleOrigensChange}
        />
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <SelectComponent
          borderRadius="8px"
          data={
            data.dataEtapasFiltro?.etapas?.map((etapa: EtapasItem) => ({
              value: etapa.codigo,
              label: obterNomeEtapaExibicao(etapa.nome, usuarioVivaz),
            })) || []
          }
          placeholder="Selecione"
          label="Etapa"
          value={data.selectedEtapa}
          disabled={!data.dataEtapasFiltro}
          onChange={data.handleEtapaChange}
        />
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <SelectAutoCompleteMultiple
          borderRadius="8px"
          data={
            data.dataSubEtapasFiltro?.subEtapas?.map((etapa: EtapasItem) => ({
              value: etapa.codigo,
              label: obterNomeEtapaExibicao(etapa.nome, usuarioVivaz),
            })) || []
          }
          placeholder="Selecione"
          label="Subetapas"
          value={data.selectedSubetapa}
          onChange={data.handleSubetapaChange}
          isError={false}
          helperText="Você pode selecionar múltiplas subetapas"
          disabled={!data.selectedEtapa || data.isFetchingSubEtapasFiltro}
        />
      </S.Row>
      <S.SpaceY space={32} />
      {showHierarchy && (
        <>
          <DivisorContainer>
            <S.SectionTitle>Responsável</S.SectionTitle>
          </DivisorContainer>
          <S.SpaceY space={32} />
          {showDiretorFilters && (
            <>
              <S.Row>
                <SelectComponent
                  borderRadius="8px"
                  data={superintendentesOptions}
                  placeholder="Selecione"
                  label="Superintendente"
                  value={data.selectedSuperintendente}
                  disabled={false}
                  onChange={data.handleSuperintendenteChange}
                />
              </S.Row>
              <S.SpaceY space={32} />
            </>
          )}
          <S.Row>
            <SelectComponent
              borderRadius="8px"
              data={equipesOptions}
              placeholder="Selecione"
              label="Equipe"
              value={data.selectedEquipe}
              disabled={showDiretorFilters && !data.selectedSuperintendente}
              onChange={data.handleEquipeChange}
            />
          </S.Row>
          <S.SpaceY space={32} />
          <S.Row>
            <SelectComponent
              borderRadius="8px"
              data={corretoresOptions}
              placeholder="Selecione"
              label="Corretor"
              value={data.selectedCorretor}
              disabled={false}
              onChange={data.handleCorretorChange}
            />
          </S.Row>
          <S.SpaceY space={32} />
        </>
      )}
      <S.Row>
        <CheckboxComponent
          checked={data.checkCompartilharProposta}
          onChange={(event) =>
            data.setCheckCompartilharProposta(event.target.checked)
          }
          label="Propostas Compartilhadas"
          color="#2D5BFF"
          removePadding
        />
      </S.Row>
      <S.SpaceY space={32} />
      <S.Row>
        <S.ButtonFilter
          disabled={
            (!data.selectedSubetapa[0] && data.selectedEtapa) ||
            (!!data.selectedTipoFiltroData &&
              (!data.dataInicio || !data.dataFim))
          }
          onClick={data.handleApplyFilters}
        >
          Aplicar Filtro
        </S.ButtonFilter>
      </S.Row>
    </S.Content>
  );
};

export default Filtro;
